package org.example.project.screens_shared

import androidx.compose.runtime.Composable
import org.example.project.dashboard.DashboardTheme
import org.example.project.enginekalender.kalenderAddMonth
import org.example.project.enginekalender.kalenderAddDays
import org.example.project.enginekalender.kalenderIsoWeekNumber
import org.example.project.enginekalender.kalenderMonthShortName
import org.example.project.enginekalender.parseKalenderIsoDate
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun KalenderDesktopNavigation(
    name: String,
    firma: String,
    position: String,
    selectedDate: String,
    onSelectDate: (String) -> Unit,
    showTaskSwitch: Boolean,
    taskMode: Boolean,
    onTaskModeChange: (Boolean) -> Unit,
    view: String,
    onViewChange: (String) -> Unit,
    showSettings: Boolean,
    settingsPlaceholder: String,
    settingsOptions: List<All>,
    onSettingsAction: (String) -> Unit
) {
    Div({ style {
        width(100.percent)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        flexWrap(FlexWrap.Wrap)
        gap(8.px)
        boxSizing("border-box")
    } }) {
        KalenderDesktopProfileItem("images/user.svg", name)
        KalenderDesktopProfileItem("images/domain.svg", firma)
        KalenderDesktopProfileItem("images/role.svg", position)

        Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px); padding(8.px, 6.px) } }) {
            KalenderDesktopMonthArrow("<") { onSelectDate(kalenderDesktopMonthIso(selectedDate, -1)) }
            (0..3).forEach { offset ->
                val monthIso = kalenderDesktopMonthIso(selectedDate, offset)
                val month = monthIso.split("-").getOrNull(1)?.toIntOrNull()
                Span({ style {
                    color(if (offset == 0) DashboardTheme.TEXT_LIGHT else DashboardTheme.TEXT_MUTED)
                    fontSize(14.px)
                    fontWeight(if (offset == 0) "900" else "700")
                    cursor("pointer")
                    property("white-space", "nowrap")
                }
                    onClick { onSelectDate(monthIso) }
                }) { Text(if (month == null) monthIso else "${kalenderMonthShortName(month)}.") }
            }
            KalenderDesktopMonthArrow(">") { onSelectDate(kalenderDesktopMonthIso(selectedDate, 1)) }
            if (showSettings) {
                Div({ style { width(190.px); flexShrink(0) } }) {
                    AppDropdown(
                        options = settingsOptions,
                        selectedValue = null,
                        onChange = onSettingsAction,
                        placeholder = settingsPlaceholder,
                        appearance = AppDropdownAppearance.Field,
                        menuMode = AppDropdownMenuMode.Overlay
                    )
                }
            }
        }

        if (view == "Woche") {
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px) } }) {
                KalenderDesktopMonthArrow("<") { shiftKalenderWeek(selectedDate, -1)?.let(onSelectDate) }
                Span({ style {
                    color(DashboardTheme.TEXT_LIGHT)
                    fontSize(13.px)
                    fontWeight("800")
                    property("white-space", "nowrap")
                } }) { Text("KW ${kalenderIsoWeekNumber(selectedDate) ?: "-"}") }
                KalenderDesktopMonthArrow(">") { shiftKalenderWeek(selectedDate, 1)?.let(onSelectDate) }
            }
        }

        if (showTaskSwitch) {
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
                KalenderDesktopModeButton("Termine", !taskMode) { onTaskModeChange(false) }
                KalenderDesktopModeButton("Aufgaben", taskMode) { onTaskModeChange(true) }
            }
        }
        Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
            KalenderDesktopModeButton("Monat", view == "Monat") { onViewChange("Monat") }
            KalenderDesktopModeButton("Woche", view == "Woche") { onViewChange("Woche") }
        }
    }
}

private fun kalenderDesktopMonthIso(value: String, delta: Int): String {
    val parts = value.split("-")
    val year = parts.getOrNull(0)?.toIntOrNull() ?: return value
    val month = parts.getOrNull(1)?.toIntOrNull() ?: return value
    return kalenderAddMonth(year, month, delta)
}

private fun shiftKalenderWeek(value: String, delta: Int): String? =
    parseKalenderIsoDate(value)?.let { date -> kalenderAddDays(date.year, date.month, date.day, delta * 7) }

@Composable
private fun KalenderDesktopModeButton(label: String, selected: Boolean, onClick: () -> Unit) {
    Button(attrs = {
        attr("type", "button")
        attr("aria-pressed", selected.toString())
        style {
            height(28.px)
            padding(0.px, 11.px)
            borderRadius(8.px)
            property("border", if (selected) "1px solid ${UISettings.ACCENT}" else "1px solid rgba(255,255,255,0.16)")
            property("background", "transparent")
            color(UISettings.STATE_BUTTON_TEXT)
            fontSize(12.px)
            fontWeight("600")
            cursor("pointer")
            property("white-space", "nowrap")
        }
        onClick { if (!selected) onClick() }
    }) { Text(label) }
}

@Composable
private fun KalenderDesktopMonthArrow(label: String, onClick: () -> Unit) {
    Div({ style { width(24.px); flexShrink(0); textAlign("center"); cursor("pointer") }
        onClick { onClick() }
    }) { Span({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(18.px); fontWeight("900") } }) { Text(label) } }
}

@Composable
private fun KalenderDesktopProfileItem(icon: String, text: String) {
    Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); padding(8.px, 10.px) } }) {
        Img(icon) { style { width(22.px); height(22.px); property("filter", DashboardTheme.ICON_FILTER_WHITE) } }
        Span({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(14.px); fontWeight("600") } }) { Text(text) }
    }
}
